import * as React from 'react';

import { Button } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import clsx from 'clsx';
import { useFormikContext } from 'formik';

const FILENAME_MAX_SIZE = 25;

type FileInputProps = {
  label: string;
  name: string;
  helperText?: React.ReactNode;
  validFormats?: string;
};

function FileInput({ name, label, helperText, validFormats }: FileInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { setFieldValue, values, errors, getFieldMeta, setFieldTouched } = useFormikContext();

  function handleClick() {
    inputRef?.current.click();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFieldTouched(name, true);
    const file = e.target.files[0];

    if (file) {
      setFieldValue(name, file);
    }
  }

  function cleanInput() {
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  }

  function handleRemove(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setFieldValue(name, null);
    cleanInput();
  }

  const value = (values as any)[name] as File | null;
  const hasValue = !!value;
  const errorMessage = (errors as any)[name] as string | null;
  const hasError = !!errorMessage && getFieldMeta(name)?.touched;

  const fileName =
    hasValue && (value.name.length > FILENAME_MAX_SIZE ? `${value.name.slice(0, FILENAME_MAX_SIZE)}...` : value.name);

  return (
    <div className='text-content-subtitle font-sans'>
      <fieldset
        className={clsx(
          'bg-surface-default relative cursor-pointer! rounded border! border-(--uizz-antd-color-border)! pt-1! pb-2.5! pl-3! hover:border-(--uizz-antd-select-hover-border-color)! focus:shadow-[0_0_0_0.2rem_var(--uizz-antd-control-outline)]! [&_label]:absolute! [&_label]:-top-5! [&_label]:left-6! [&_label]:text-xs! [&_label]:font-semibold! [&_legend]:invisible! [&_legend]:ml-2! [&_legend]:text-[[0.83125rem]!',
          hasError && 'border-(--uizz-antd-color-error)! [&_label]:text-(--uizz-antd-color-error)!'
        )}
        onClick={handleClick}
        role='button'
      >
        <label className='-mt-1' htmlFor={name}>
          {label}
        </label>
        <legend className='w-auto!'>{label}</legend>
        <input id={name} onChange={handleChange} ref={inputRef} type='file' hidden accept={validFormats} />
        <div className='relative -mt-0.5 flex items-center text-sm uppercase'>
          <Icon.CloudUpload size={20} className='mr-3 size-5 text-xl'  />
          {fileName || 'Selecione um arquivo'}
          {hasValue && (
            <Button
              type='text'
              className='absolute! right-3!'
              aria-label='Remover arquivo'
              onClick={handleRemove}
              icon={<Icon.X size={20} className='size-5 text-xl'  />}
            />
          )}
        </div>
      </fieldset>
      <div className='mt-2 font-sans text-[[0.625rem]'>
        {hasError ? (
          <p role='alert' className='text-(--uizz-antd-color-error)'>
            {errorMessage}
          </p>
        ) : (
          <>
            {typeof helperText === 'string' ? (
              <p role='alert' className='text-primary'>
                {helperText}
              </p>
            ) : (
              helperText
            )}
          </>
        )}
      </div>
    </div>
  );
}

export { FileInput };
